/**
 * Frontend helper that creates EntityDef objects from schema entities.
 *
 * This lives in src/ (not party/) because it needs the ENTITY_KIND symbol
 * and EntityDef types from create-sync-api.ts. The party/ build can't
 * import from src/ due to different build targets (Wrangler vs Vite).
 */

import {
  type ArrayEntityApi,
  type ArrayEntityDef,
  type ArrayEntityOps,
  type CollectionEntityDef,
  ENTITY_KIND,
  type ExtensionFactory,
  type RecordEntityApi,
  type RecordEntityDef,
  type RecordEntityOps,
  type ValueEntityDef,
} from './create-sync-api';

type EntityKind = 'array' | 'collection' | 'value' | 'record';

type AnyExt = Record<string, (...args: any[]) => any>;

/** Schema entity shape (matches EntityResult from sync-schema.ts). */
type SchemaEntity<T = unknown, Kind extends EntityKind = EntityKind> = {
  readonly config: {
    readonly name: string;
    readonly type: Kind;
    readonly primaryKey?: string;
  };
  readonly entity: { stateKey: string };
  readonly T: T;
};

/** Conditional return type: maps Kind → the correct EntityDef type. */
type DefResult<T, Kind extends EntityKind, Ext extends AnyExt> = Kind extends 'array'
  ? ArrayEntityDef<T, Ext>
  : Kind extends 'collection'
    ? CollectionEntityDef<T, Ext>
    : Kind extends 'value'
      ? ValueEntityDef<T>
      : Kind extends 'record'
        ? RecordEntityDef<T, Ext>
        : never;

/**
 * The factory signature handed to `def(…, { extensions })`, dispatched on
 * entity kind: array/collection get `ArrayEntityOps`, record gets
 * `RecordEntityOps`, and value entities don't support extensions.
 */
type DefExtensionFactory<T, Kind extends EntityKind, Ext extends AnyExt> = Kind extends
  | 'array'
  | 'collection'
  ? ExtensionFactory<ArrayEntityOps<T>, ArrayEntityApi<T>, Ext>
  : Kind extends 'record'
    ? ExtensionFactory<RecordEntityOps<T>, RecordEntityApi<T>, Ext>
    : never;

/**
 * Create a frontend EntityDef from a schema entity.
 *
 * Extensions are declared as a **factory** `(ops, self) => ({...})`. `ops`
 * is the clean mutation surface; `self` is the full entity API (includes
 * reactive hooks — avoid calling those from event-handler extension bodies).
 * Mutations through `ops` auto-stamp `updatedAtISO`, persist to IndexedDB,
 * and push to the server.
 *
 * ```ts
 * folders: def(schema.folders, {
 *   extensions: (ops) => ({
 *     moveToParent: (id, parentId) => ops.update(id, { parentFolder: parentId }),
 *   }),
 * }),
 * settings: def(schema.settings),
 * ```
 *
 * Overloads: matches the pattern in `create-sync-api.ts` — without overloads,
 * omitting `extensions` causes TypeScript to mis-infer `Ext` and collapse the
 * entire entity API to `any`. With these overloads, omitting the opts arg
 * cleanly defaults `Ext` to `Record<never, never>`.
 */
export function def<T, Kind extends EntityKind, Ext extends AnyExt>(
  schemaEntity: SchemaEntity<T, Kind>,
  opts: { extensions: DefExtensionFactory<T, Kind, Ext> },
): DefResult<T, Kind, Ext>;
export function def<T, Kind extends EntityKind>(
  schemaEntity: SchemaEntity<T, Kind>,
): DefResult<T, Kind, Record<never, never>>;
export function def<T, Kind extends EntityKind, Ext extends AnyExt>(
  schemaEntity: SchemaEntity<T, Kind>,
  opts?: { extensions?: DefExtensionFactory<T, Kind, Ext> },
): DefResult<T, Kind, Ext> {
  const { config, entity } = schemaEntity;
  const isArrayLike = config.type === 'array' || config.type === 'collection';

  return {
    [ENTITY_KIND]: config.type,
    config: {
      key: entity.stateKey,
      ...(isArrayLike && config.primaryKey ? { primaryKey: config.primaryKey } : {}),
      ...(opts?.extensions ? { extensions: opts.extensions } : {}),
    },
  } as DefResult<T, Kind, Ext>;
}
